import { useEffect, useRef, useState, type ReactNode } from 'react';
import { seesEveryContact } from '@ehr/shared';
import { useSession } from '../../auth/session.js';
import { apiErrorText } from '../../lib/api.js';
import { showError } from '../../lib/dialogs.js';
import { fetchAccessPeople, fetchMyAccess, type AccessPerson } from '../../lib/contactAccess.js';
import { ManageAccessModal } from './ManageAccessModal.js';
import { ReassignModal } from './ReassignModal.js';
import { SaMessageCard, saMessage, type SaMessage } from './SaModal.js';
import { ShareFlow } from './ShareFlow.js';
import { capFrom, type ShareCap, type ShareChange, type ShareTarget } from './shareState.js';

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11): one place that opens Share, Reassign and Who has access for a
 * contact (the chart's ⋮ menu and owner chip) or for the contacts ticked on the Contacts list. The
 * caller renders `element` once and calls the openers; `onChanged` runs after anything is saved
 * (reload the list, the owner, the caller's own access). Saved messages sit bottom right.
 */
type Dialog =
  | {
      kind: 'share';
      targets: ShareTarget[];
      cap: ShareCap;
      change?: ShareChange;
      back?: ShareTarget;
    }
  | { kind: 'reassign'; targets: ShareTarget[]; presetUserId?: string; back?: ShareTarget }
  | { kind: 'manage'; target: ShareTarget };

const NO_CAP: ShareCap = { tabs: null, rights: { share: true, reassign: true, archive: true } };

export function useContactAccessDialogs(opts: { onChanged?: () => void } = {}): {
  openShare: (targets: ShareTarget[]) => void;
  openReassign: (targets: ShareTarget[]) => void;
  openManage: (target: ShareTarget) => void;
  element: ReactNode;
} {
  const { user } = useSession();
  const [dlg, setDlg] = useState<Dialog | null>(null);
  const [people, setPeople] = useState<AccessPerson[] | null>(null);
  const [toast, setToast] = useState<SaMessage | null>(null);
  const changedRef = useRef(opts.onChanged);
  useEffect(() => {
    changedRef.current = opts.onChanged;
  });
  const toastId = toast?.id;
  useEffect(() => {
    if (toastId === undefined) return;
    const t = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(t);
  }, [toastId]);

  const ensurePeople = async () => {
    if (people) return people;
    const p = await fetchAccessPeople();
    setPeople(p);
    return p;
  };
  const capFor = async (targets: ShareTarget[]): Promise<ShareCap> =>
    seesEveryContact(user?.role)
      ? NO_CAP
      : capFrom(await Promise.all(targets.map((t) => fetchMyAccess(t.id))));
  const failed = (e: unknown) => showError(apiErrorText(e, 'Could not open that. Try again.'));

  const share = (targets: ShareTarget[], change?: ShareChange, back?: ShareTarget) => {
    setToast(null);
    Promise.all([ensurePeople(), capFor(targets)])
      .then(([, cap]) => setDlg({ kind: 'share', targets, cap, change, back }))
      .catch(failed);
  };
  const reassign = (targets: ShareTarget[], presetUserId?: string, back?: ShareTarget) => {
    setToast(null);
    ensurePeople()
      .then(() => setDlg({ kind: 'reassign', targets, presetUserId, back }))
      .catch(failed);
  };
  const manage = (target: ShareTarget) => {
    setToast(null);
    ensurePeople()
      .then(() => setDlg({ kind: 'manage', target }))
      .catch(failed);
  };

  const saved = (message: string, back?: ShareTarget) => {
    setToast(saMessage(message));
    changedRef.current?.();
    if (!back) {
      setDlg(null);
      return;
    }
    // back to Who has access, if the change (a reassign) left the caller able to see it
    fetchMyAccess(back.id)
      .then((m) => setDlg(m.canShare ? { kind: 'manage', target: back } : null))
      .catch(() => setDlg(null));
  };
  const cancel = (back?: ShareTarget) => setDlg(back ? { kind: 'manage', target: back } : null);

  let body: ReactNode = null;
  if (dlg?.kind === 'share' && people)
    body = (
      <ShareFlow
        targets={dlg.targets}
        cap={dlg.cap}
        people={people}
        meId={user?.id}
        change={dlg.change}
        onCancel={() => cancel(dlg.back)}
        onSaved={(m) => saved(m, dlg.back)}
      />
    );
  else if (dlg?.kind === 'reassign' && people)
    body = (
      <ReassignModal
        targets={dlg.targets}
        people={people}
        presetUserId={dlg.presetUserId}
        onCancel={() => cancel(dlg.back)}
        onDone={(m) => saved(m, dlg.back)}
      />
    );
  else if (dlg?.kind === 'manage') {
    const t = dlg.target;
    body = (
      <ManageAccessModal
        contactId={t.id}
        onClose={() => setDlg(null)}
        onShare={() => share([t], undefined, t)}
        onChangeUser={(row) =>
          share([t], { kind: 'user', userId: row.userId, tabs: row.tabs, rights: row.rights }, t)
        }
        onChangeRole={(row) =>
          share(
            [t],
            {
              kind: 'role',
              role: row.role,
              excluded: row.excluded.map((x) => x.id),
              tabs: row.tabs,
              rights: row.rights,
            },
            t,
          )
        }
        onReassign={(presetUserId, ownerUserId) => {
          // the owner may have changed since the chart opened: Who has access read it fresh
          const fresh = { ...t, ownerUserId };
          reassign([fresh], presetUserId, fresh);
        }}
        onChanged={() => changedRef.current?.()}
      />
    );
  }

  return {
    openShare: (targets) => share(targets),
    openReassign: (targets) => reassign(targets),
    openManage: manage,
    element: (
      <>
        {body}
        {toast && (
          <div className="acs-toast">
            <SaMessageCard msg={toast} />
          </div>
        )}
      </>
    ),
  };
}
